import {useGetInfinitePatientsTherapiesSequences, useTherapyDateActionsCount} from '@/modules/patients-therapies/api'
import {PatientsTherapiesSequencesTable} from '@/modules/patients-therapies/components/patients-therapies-table'
import {PatientsTherapiesFiltersSchema} from '@/modules/patients-therapies/schemas'
import {useGetSettings} from '@/modules/settings/api'
import {isPatientArrivalEnabled} from '@/modules/settings/functions'
import {Box} from '@chakra-ui/react'
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {PatientTherapyList} from '@/modules/patients-therapies/components/patient-therapy-list'
import {useGetPatient} from '@/modules/patients/api'
import {ActivityFilters} from '@/modules/patients-therapies/components'
import * as z from 'zod'

const ActivitySearchSchema = z.object({
  filters: PatientsTherapiesFiltersSchema.optional().default({
    therapyDate: [new Date().toISOString().split('T')[0]],
  }),
})

export const Route = createFileRoute('/activity/')({
  component: ActivityPage,
  validateSearch: ActivitySearchSchema,
})

const LIMIT = 50

function ActivityPage() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const {actionsCount} = useTherapyDateActionsCount({
    therapyDate: search.filters?.therapyDate?.[0],
  })
  const [selectedPatient] = useGetPatient(search.filters?.patientId)
  const {settings} = useGetSettings()
  // gates the doctor/nurse pending-arrival action
  const patientArrivalEnabled = isPatientArrivalEnabled(settings)

  const infinite = useGetInfinitePatientsTherapiesSequences({
    filters: {...search.filters, patientArrivalEnabled},
    limit: LIMIT,
  })

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = infinite

  function onNavigate(filters: PatientsTherapiesFiltersSchema) {
    navigate({
      to: '/activity',
      search: prev => ({
        ...prev,
        filters: {
          search: filters.search,
          status: filters.status,
          wards: filters.wards,
          sections: filters.sections,
          patientId: filters.patientId,
          onlyCurrentUserActions: filters.onlyCurrentUserActions,
          therapyDate: filters.therapyDate?.map(date => date.toString()) || [],
        },
      }),
    })
  }

  function clearPatient() {
    navigate({
      to: '/activity',
      from: '/activity/',
      search: prev => ({...prev, filters: {...prev.filters, patientId: undefined}}),
    })
  }

  return (
    <>
      <ActivityFilters
        actionsCount={actionsCount}
        searchFilters={search.filters}
        onNavigate={onNavigate}
        onClearPatient={clearPatient}
        selectedPatient={selectedPatient}
      />

      <Box display={{base: 'block', md: 'none'}} height="100%">
        <PatientTherapyList virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}} />
      </Box>

      <PatientsTherapiesSequencesTable
        virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
        table={{}}
        dateFilterActive={Boolean(search.filters?.therapyDate?.length)}
        containerProps={{display: {base: 'none', md: 'flex'}}}
      />
    </>
  )
}
